import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Filter,
    BookOpen,
    Video,
    Download,
    Bookmark,
    TrendingUp,
    AlertCircle,
    X,
    Plus
} from 'lucide-react';
import ResourceCard from '../../components/resources/ResourceCard';
import resourceService from '../../services/resource.service';
import { useAuth } from '../../contexts/AuthContext';

const ResourcesPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedContentType, setSelectedContentType] = useState('');
    const [categories, setCategories] = useState([]);
    const [featuredResources, setFeaturedResources] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await resourceService.getCategoriesWithCounts();
                setCategories(response.data || []);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };

        fetchCategories();
    }, []);

    // Fetch featured resources
    useEffect(() => {
        const fetchFeatured = async () => {
            try {
                const response = await resourceService.getFeaturedResources(3);
                setFeaturedResources(response.data || []);
            } catch (err) {
                console.error('Error fetching featured resources:', err);
            }
        };

        fetchFeatured();
    }, []);

    // Fetch resources with filters
    useEffect(() => {
        const fetchResources = async () => {
            try {
                setLoading(true);
                const filters = {
                    search: searchTerm,
                    category: selectedCategory,
                    contentType: selectedContentType,
                    sort: '-views'
                };

                const response = await resourceService.getResources(filters);
                setResources(response.data || []);
            } catch (err) {
                setError(err.message || 'Failed to load resources');
            } finally {
                setLoading(false);
            }
        };

        fetchResources();
    }, [searchTerm, selectedCategory, selectedContentType]);

    const handleBookmark = async (resourceId) => {
        try {
            await resourceService.toggleBookmark(resourceId);
            // Optionally show notification
        } catch (err) {
            console.error('Error bookmarking resource:', err);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
        setSelectedContentType('');
    };

    const formatCategoryName = (category) => {
        return category
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const contentTypes = [
        { value: 'article', label: 'Articles', icon: BookOpen },
        { value: 'video', label: 'Videos', icon: Video },
        { value: 'download', label: 'Downloads', icon: Download },
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-800 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-4xl font-bold mb-4">Mental Health Resources</h1>
                            <p className="text-blue-100 text-lg max-w-2xl">
                                Explore our comprehensive collection of mental health resources designed specifically for university students in Kenya
                            </p>
                        </div>
                        {['admin', 'counselor', 'hod'].includes(user?.role) && (
                            <button
                                onClick={() => navigate(`/${user.role}/resources/new`)}
                                className="self-start md:self-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium shadow-sm"
                            >
                                <Plus className="h-5 w-5" />
                                Create Resource
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Search and Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                    <div className="flex flex-col lg:flex-row gap-4">
                        {/* Search Bar */}
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search resources..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Filter Button (Mobile) */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="lg:hidden flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <Filter className="h-5 w-5" />
                            Filters
                        </button>
                    </div>

                    {/* Filters (Desktop always visible, Mobile toggleable) */}
                    <div className={`mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4 ${showFilters ? 'block' : 'hidden lg:grid'}`}>
                        {/* Category Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category
                            </label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">All Categories</option>
                                {categories.map((cat) => (
                                    <option key={cat.category} value={cat.category}>
                                        {formatCategoryName(cat.category)} ({cat.count})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Content Type Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Content Type
                            </label>
                            <select
                                value={selectedContentType}
                                onChange={(e) => setSelectedContentType(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">All Types</option>
                                {contentTypes.map((type) => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Clear Filters */}
                        <div className="flex items-end">
                            <button
                                onClick={clearFilters}
                                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                                <X className="h-4 w-4" />
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Featured Resources */}
                {featuredResources.length > 0 && (
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="h-5 w-5 text-yellow-500" />
                            <h2 className="text-2xl font-bold text-gray-900">Featured Resources</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {featuredResources.map((resource) => (
                                <ResourceCard
                                    key={resource._id}
                                    resource={resource}
                                    onBookmark={handleBookmark}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* All Resources */}
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {selectedCategory
                                ? formatCategoryName(selectedCategory)
                                : 'All Resources'}
                        </h2>
                        <span className="text-sm text-gray-600">
                            {resources.length} {resources.length === 1 ? 'resource' : 'resources'}
                        </span>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                            <div className="flex">
                                <AlertCircle className="h-5 w-5 text-red-400" />
                                <p className="ml-3 text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Resources Grid */}
                    {!loading && !error && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {resources.map((resource) => (
                                <ResourceCard
                                    key={resource._id}
                                    resource={resource}
                                    onBookmark={handleBookmark}
                                />
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && !error && resources.length === 0 && (
                        <div className="text-center py-12">
                            <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No resources found</h3>
                            <p className="text-gray-600">
                                Try adjusting your filters or search term
                            </p>
                        </div>
                    )}
                </div>

                {/* Crisis Support Banner */}
                <div className="mt-12 bg-red-50 border-2 border-red-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                        <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="text-lg font-semibold text-red-900 mb-2">
                                Need Immediate Help?
                            </h3>
                            <p className="text-red-800 mb-3">
                                If you're experiencing a mental health crisis or emergency, please reach out for immediate support:
                            </p>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-red-900">Befrienders Kenya:</span>
                                    <a href="tel:0800720648" className="text-red-700 hover:text-red-900 underline">
                                        0800 720 648
                                    </a>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-red-900">Emergency Services:</span>
                                    <a href="tel:999" className="text-red-700 hover:text-red-900 underline">
                                        999 / 112
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResourcesPage;
